// Supabase Service Layer for BlinD Application
// Handles all database operations with snake_case to camelCase conversion

import { supabase } from '../lib/supabase';
import { emailNotificationService } from './emailNotificationService';

// ============ CANDIDATES SERVICE ============
export const candidatesService = {
  async getAll() {
    const { data, error } = await supabase?.from('candidates')?.select('*')?.eq('is_active', true)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      title: row?.title,
      summary: row?.summary,
      experience: row?.experience,
      location: row?.location,
      industry: row?.industry,
      seniority: row?.seniority,
      matchScore: row?.match_score,
      profileStrength: row?.profile_strength,
      isNew: row?.is_new,
      skills: row?.skills,
      teamSize: row?.team_size,
      budgetManaged: row?.budget_managed,
      plResponsibility: row?.pl_responsibility,
      yearsOfExperience: row?.years_of_experience,
      workMode: row?.work_mode,
      language: row?.language,
      isActive: row?.is_active,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  async getById(id) {
    const { data, error } = await supabase?.from('candidates')?.select('*')?.eq('id', id)?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      userId: data?.user_id,
      title: data?.title,
      summary: data?.summary,
      experience: data?.experience,
      location: data?.location,
      industry: data?.industry,
      seniority: data?.seniority,
      matchScore: data?.match_score,
      profileStrength: data?.profile_strength,
      isNew: data?.is_new,
      skills: data?.skills,
      teamSize: data?.team_size,
      budgetManaged: data?.budget_managed,
      plResponsibility: data?.pl_responsibility,
      yearsOfExperience: data?.years_of_experience,
      workMode: data?.work_mode,
      language: data?.language,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async create(candidateData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('candidates')?.insert({
        user_id: user?.id,
        title: candidateData?.title,
        summary: candidateData?.summary,
        experience: candidateData?.experience,
        location: candidateData?.location,
        industry: candidateData?.industry,
        seniority: candidateData?.seniority,
        match_score: candidateData?.matchScore || 0,
        profile_strength: candidateData?.profileStrength || 0,
        is_new: candidateData?.isNew !== undefined ? candidateData?.isNew : true,
        skills: candidateData?.skills || [],
        team_size: candidateData?.teamSize,
        budget_managed: candidateData?.budgetManaged,
        pl_responsibility: candidateData?.plResponsibility,
        years_of_experience: candidateData?.yearsOfExperience,
        work_mode: candidateData?.workMode,
        language: candidateData?.language
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      userId: data?.user_id,
      title: data?.title,
      summary: data?.summary,
      experience: data?.experience,
      location: data?.location,
      industry: data?.industry,
      seniority: data?.seniority,
      matchScore: data?.match_score,
      profileStrength: data?.profile_strength,
      isNew: data?.is_new,
      skills: data?.skills,
      teamSize: data?.team_size,
      budgetManaged: data?.budget_managed,
      plResponsibility: data?.pl_responsibility,
      yearsOfExperience: data?.years_of_experience,
      workMode: data?.work_mode,
      language: data?.language,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async update(id, updates) {
    const dbUpdates = {};
    if (updates?.title !== undefined) dbUpdates.title = updates?.title;
    if (updates?.summary !== undefined) dbUpdates.summary = updates?.summary;
    if (updates?.experience !== undefined) dbUpdates.experience = updates?.experience;
    if (updates?.location !== undefined) dbUpdates.location = updates?.location;
    if (updates?.industry !== undefined) dbUpdates.industry = updates?.industry;
    if (updates?.seniority !== undefined) dbUpdates.seniority = updates?.seniority;
    if (updates?.matchScore !== undefined) dbUpdates.match_score = updates?.matchScore;
    if (updates?.profileStrength !== undefined) dbUpdates.profile_strength = updates?.profileStrength;
    if (updates?.isNew !== undefined) dbUpdates.is_new = updates?.isNew;
    if (updates?.skills !== undefined) dbUpdates.skills = updates?.skills;
    if (updates?.teamSize !== undefined) dbUpdates.team_size = updates?.teamSize;
    if (updates?.budgetManaged !== undefined) dbUpdates.budget_managed = updates?.budgetManaged;
    if (updates?.plResponsibility !== undefined) dbUpdates.pl_responsibility = updates?.plResponsibility;
    if (updates?.yearsOfExperience !== undefined) dbUpdates.years_of_experience = updates?.yearsOfExperience;
    if (updates?.workMode !== undefined) dbUpdates.work_mode = updates?.workMode;
    if (updates?.language !== undefined) dbUpdates.language = updates?.language;
    if (updates?.isActive !== undefined) dbUpdates.is_active = updates?.isActive;

    const { data, error } = await supabase?.from('candidates')?.update(dbUpdates)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      userId: data?.user_id,
      title: data?.title,
      summary: data?.summary,
      experience: data?.experience,
      location: data?.location,
      industry: data?.industry,
      seniority: data?.seniority,
      matchScore: data?.match_score,
      profileStrength: data?.profile_strength,
      isNew: data?.is_new,
      skills: data?.skills,
      teamSize: data?.team_size,
      budgetManaged: data?.budget_managed,
      plResponsibility: data?.pl_responsibility,
      yearsOfExperience: data?.years_of_experience,
      workMode: data?.work_mode,
      language: data?.language,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  }
};

// ============ OPPORTUNITIES SERVICE ============
export const opportunitiesService = {
  async getAll() {
    const { data, error } = await supabase?.from('opportunities')?.select('*, employers(*)')?.eq('is_active', true)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(row => ({
      id: row?.id,
      employerId: row?.employer_id,
      companyDescription: row?.company_description,
      roleTitle: row?.role_title,
      compensationRange: row?.compensation_range,
      location: row?.location,
      workMode: row?.work_mode,
      teamSize: row?.team_size,
      growthStage: row?.growth_stage,
      employeeCount: row?.employee_count,
      industry: row?.industry,
      benefits: row?.benefits,
      isExceptional: row?.is_exceptional,
      isActive: row?.is_active,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  async getById(id) {
    const { data, error } = await supabase?.from('opportunities')?.select('*, employers(*)')?.eq('id', id)?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      employerId: data?.employer_id,
      companyDescription: data?.company_description,
      roleTitle: data?.role_title,
      compensationRange: data?.compensation_range,
      location: data?.location,
      workMode: data?.work_mode,
      teamSize: data?.team_size,
      growthStage: data?.growth_stage,
      employeeCount: data?.employee_count,
      industry: data?.industry,
      benefits: data?.benefits,
      isExceptional: data?.is_exceptional,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async create(opportunityData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get employer profile
    const { data: employer } = await supabase?.from('employers')?.select('id')?.eq('user_id', user?.id)?.single();

    if (!employer) throw new Error('Employer profile not found');

    const { data, error } = await supabase?.from('opportunities')?.insert({
        employer_id: employer?.id,
        company_description: opportunityData?.companyDescription,
        role_title: opportunityData?.roleTitle,
        compensation_range: opportunityData?.compensationRange,
        location: opportunityData?.location,
        work_mode: opportunityData?.workMode,
        team_size: opportunityData?.teamSize,
        growth_stage: opportunityData?.growthStage,
        employee_count: opportunityData?.employeeCount,
        industry: opportunityData?.industry,
        benefits: opportunityData?.benefits || [],
        is_exceptional: opportunityData?.isExceptional || false
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      employerId: data?.employer_id,
      companyDescription: data?.company_description,
      roleTitle: data?.role_title,
      compensationRange: data?.compensation_range,
      location: data?.location,
      workMode: data?.work_mode,
      teamSize: data?.team_size,
      growthStage: data?.growth_stage,
      employeeCount: data?.employee_count,
      industry: data?.industry,
      benefits: data?.benefits,
      isExceptional: data?.is_exceptional,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async update(id, updates) {
    const dbUpdates = {};
    if (updates?.companyDescription !== undefined) dbUpdates.company_description = updates?.companyDescription;
    if (updates?.roleTitle !== undefined) dbUpdates.role_title = updates?.roleTitle;
    if (updates?.compensationRange !== undefined) dbUpdates.compensation_range = updates?.compensationRange;
    if (updates?.location !== undefined) dbUpdates.location = updates?.location;
    if (updates?.workMode !== undefined) dbUpdates.work_mode = updates?.workMode;
    if (updates?.teamSize !== undefined) dbUpdates.team_size = updates?.teamSize;
    if (updates?.growthStage !== undefined) dbUpdates.growth_stage = updates?.growthStage;
    if (updates?.employeeCount !== undefined) dbUpdates.employee_count = updates?.employeeCount;
    if (updates?.industry !== undefined) dbUpdates.industry = updates?.industry;
    if (updates?.benefits !== undefined) dbUpdates.benefits = updates?.benefits;
    if (updates?.isExceptional !== undefined) dbUpdates.is_exceptional = updates?.isExceptional;
    if (updates?.isActive !== undefined) dbUpdates.is_active = updates?.isActive;

    const { data, error } = await supabase?.from('opportunities')?.update(dbUpdates)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      employerId: data?.employer_id,
      companyDescription: data?.company_description,
      roleTitle: data?.role_title,
      compensationRange: data?.compensation_range,
      location: data?.location,
      workMode: data?.work_mode,
      teamSize: data?.team_size,
      growthStage: data?.growth_stage,
      employeeCount: data?.employee_count,
      industry: data?.industry,
      benefits: data?.benefits,
      isExceptional: data?.is_exceptional,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  }
};

// ============ MATCHES SERVICE ============
export const matchesService = {
  async getByUserId(userId) {
    const { data, error } = await supabase?.from('matches')?.select(`
        *,
        candidates(*),
        employers(*),
        opportunities(*)
      `)?.or(`candidates.user_id.eq.${userId},employers.user_id.eq.${userId}`)?.eq('archived', false)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(row => ({
      id: row?.id,
      candidateId: row?.candidate_id,
      employerId: row?.employer_id,
      opportunityId: row?.opportunity_id,
      status: row?.status,
      matchScore: row?.match_score,
      isRevealed: row?.is_revealed,
      revealedAt: row?.revealed_at,
      archived: row?.archived,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      candidate: row?.candidates,
      employer: row?.employers,
      opportunity: row?.opportunities
    }));
  },

  async create(matchData) {
    const { data, error } = await supabase?.from('matches')?.insert({
        candidate_id: matchData?.candidateId,
        employer_id: matchData?.employerId,
        opportunity_id: matchData?.opportunityId,
        status: matchData?.status || 'pending',
        match_score: matchData?.matchScore || 0
      })?.select(`
        *,
        candidates(*, user_profiles(*)),
        employers(*, user_profiles(*)),
        opportunities(*)
      `)?.single();
    
    if (error) throw error;

    // Send email notifications to both candidate and employer
    try {
      // Get user preferences for both parties
      const candidatePrefs = await userPreferencesService?.get(data?.candidates?.user_id);
      const employerPrefs = await userPreferencesService?.get(data?.employers?.user_id);

      // Send to candidate if email notifications enabled
      if (candidatePrefs?.notificationSettings?.emailNotifications && 
          candidatePrefs?.notificationSettings?.matchAlerts) {
        await emailNotificationService?.sendMatchEmail({
          recipientEmail: data?.candidates?.user_profiles?.email,
          recipientName: data?.candidates?.user_profiles?.full_name,
          matchDetails: {
            roleTitle: data?.opportunities?.role_title,
            companyDescription: data?.opportunities?.company_description,
            location: data?.opportunities?.location,
            compensation: data?.opportunities?.compensation_range,
            matchScore: data?.match_score,
            actionUrl: `${window?.location?.origin}/match-management`
          },
          userRole: 'candidate'
        });
      }

      // Send to employer if email notifications enabled
      if (employerPrefs?.notificationSettings?.emailNotifications && 
          employerPrefs?.notificationSettings?.matchAlerts) {
        await emailNotificationService?.sendMatchEmail({
          recipientEmail: data?.employers?.user_profiles?.email,
          recipientName: data?.employers?.user_profiles?.full_name,
          matchDetails: {
            candidateTitle: data?.candidates?.title,
            experience: data?.candidates?.experience,
            location: data?.candidates?.location,
            industry: data?.candidates?.industry,
            matchScore: data?.match_score,
            actionUrl: `${window?.location?.origin}/employer-dashboard`
          },
          userRole: 'employer'
        });
      }
    } catch (emailError) {
      console.error('Error sending match email notifications:', emailError);
      // Don't fail the match creation if email fails
    }
    
    return {
      id: data?.id,
      candidateId: data?.candidate_id,
      employerId: data?.employer_id,
      opportunityId: data?.opportunity_id,
      status: data?.status,
      matchScore: data?.match_score,
      isRevealed: data?.is_revealed,
      revealedAt: data?.revealed_at,
      archived: data?.archived,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async update(id, updates) {
    const dbUpdates = {};
    if (updates?.status !== undefined) dbUpdates.status = updates?.status;
    if (updates?.matchScore !== undefined) dbUpdates.match_score = updates?.matchScore;
    if (updates?.isRevealed !== undefined) dbUpdates.is_revealed = updates?.isRevealed;
    if (updates?.revealedAt !== undefined) dbUpdates.revealed_at = updates?.revealedAt;
    if (updates?.archived !== undefined) dbUpdates.archived = updates?.archived;

    const { data, error } = await supabase?.from('matches')?.update(dbUpdates)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      candidateId: data?.candidate_id,
      employerId: data?.employer_id,
      opportunityId: data?.opportunity_id,
      status: data?.status,
      matchScore: data?.match_score,
      isRevealed: data?.is_revealed,
      revealedAt: data?.revealed_at,
      archived: data?.archived,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  }
};

// ============ NOTIFICATIONS SERVICE ============
export const notificationsService = {
  async getByUserId(userId) {
    const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      type: row?.type,
      title: row?.title,
      message: row?.message,
      link: row?.link,
      isRead: row?.is_read,
      metadata: row?.metadata,
      createdAt: row?.created_at
    }));
  },

  async create(notificationData) {
    const { data, error } = await supabase?.from('notifications')?.insert({
        user_id: notificationData?.userId,
        type: notificationData?.type,
        title: notificationData?.title,
        message: notificationData?.message,
        link: notificationData?.link,
        metadata: notificationData?.metadata || {}
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      userId: data?.user_id,
      type: data?.type,
      title: data?.title,
      message: data?.message,
      link: data?.link,
      isRead: data?.is_read,
      metadata: data?.metadata,
      createdAt: data?.created_at
    };
  },

  async markAsRead(id) {
    const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', id);
    
    if (error) throw error;
  },

  async markAllAsRead(userId) {
    const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('user_id', userId)?.eq('is_read', false);
    
    if (error) throw error;
  }
};

// ============ MESSAGES SERVICE ============
export const messagesService = {
  async getByConversationId(conversationId) {
    const { data, error } = await supabase?.from('messages')?.select('*, user_profiles(full_name, avatar_url)')?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });
    
    if (error) throw error;
    return data?.map(row => ({
      id: row?.id,
      conversationId: row?.conversation_id,
      senderId: row?.sender_id,
      content: row?.content,
      status: row?.status,
      readAt: row?.read_at,
      createdAt: row?.created_at,
      sender: row?.user_profiles
    }));
  },

  async create(messageData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('messages')?.insert({
        conversation_id: messageData?.conversationId,
        sender_id: user?.id,
        content: messageData?.content
      })?.select('*, user_profiles(*)')?.single();
    
    if (error) throw error;

    // Update conversation last_message_at
    await supabase?.from('conversations')?.update({ last_message_at: new Date()?.toISOString() })?.eq('id', messageData?.conversationId);

    // Send email notification to recipient
    try {
      // Get conversation details to find recipient
      const { data: conversation } = await supabase?.from('conversations')
        ?.select('*, candidate_id, employer_id, candidates(user_profiles(*)), employers(user_profiles(*))')
        ?.eq('id', messageData?.conversationId)
        ?.single();

      if (conversation) {
        // Determine recipient (the one who didn't send the message)
        const recipientProfile = conversation?.candidates?.user_profiles?.id === user?.id 
          ? conversation?.employers?.user_profiles 
          : conversation?.candidates?.user_profiles;

        if (recipientProfile) {
          // Get recipient preferences
          const recipientPrefs = await userPreferencesService?.get(recipientProfile?.id);

          // Send email if notifications enabled
          if (recipientPrefs?.notificationSettings?.emailNotifications && 
              recipientPrefs?.notificationSettings?.messageAlerts) {
            await emailNotificationService?.sendMessageEmail({
              recipientEmail: recipientProfile?.email,
              recipientName: recipientProfile?.full_name,
              senderName: data?.user_profiles?.full_name,
              messagePreview: messageData?.content?.substring(0, 100) + (messageData?.content?.length > 100 ? '...' : ''),
              conversationUrl: `${window?.location?.origin}/secure-chat-interface?conversation=${messageData?.conversationId}`
            });
          }
        }
      }
    } catch (emailError) {
      console.error('Error sending message email notification:', emailError);
      // Don't fail the message creation if email fails
    }

    return {
      id: data?.id,
      conversationId: data?.conversation_id,
      senderId: data?.sender_id,
      content: data?.content,
      status: data?.status,
      readAt: data?.read_at,
      createdAt: data?.created_at
    };
  },

  async markAsRead(messageId) {
    const { error } = await supabase?.from('messages')?.update({ 
        status: 'read',
        read_at: new Date()?.toISOString()
      })?.eq('id', messageId);
    
    if (error) throw error;
  }
};

// ============ CONVERSATIONS SERVICE ============
export const conversationsService = {
  async getByUserId(userId) {
    const { data, error } = await supabase?.from('conversations')?.select(`
        *,
        matches(*),
        candidates(*, user_profiles(*)),
        employers(*, user_profiles(*))
      `)?.or(`candidates.user_id.eq.${userId},employers.user_id.eq.${userId}`)?.eq('is_archived', false)?.order('last_message_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(row => ({
      id: row?.id,
      matchId: row?.match_id,
      candidateId: row?.candidate_id,
      employerId: row?.employer_id,
      lastMessageAt: row?.last_message_at,
      isArchived: row?.is_archived,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      candidate: row?.candidates,
      employer: row?.employers,
      match: row?.matches
    }));
  },

  async create(conversationData) {
    const { data, error } = await supabase?.from('conversations')?.insert({
        match_id: conversationData?.matchId,
        candidate_id: conversationData?.candidateId,
        employer_id: conversationData?.employerId
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      matchId: data?.match_id,
      candidateId: data?.candidate_id,
      employerId: data?.employer_id,
      lastMessageAt: data?.last_message_at,
      isArchived: data?.is_archived,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  }
};

// ============ SAVED FILTERS SERVICE ============
export const savedFiltersService = {
  async getAll(userId, context) {
    let query = supabase?.from('saved_filters')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (context) {
      query = query?.eq('context', context);
    }
    
    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      name: row?.name,
      filterData: row?.filter_data,
      context: row?.context,
      createdAt: row?.created_at
    }));
  },

  async create(userId, name, filterData, context) {
    const { data, error } = await supabase?.from('saved_filters')?.insert({
        user_id: userId,
        name,
        filter_data: filterData,
        context
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      userId: data?.user_id,
      name: data?.name,
      filterData: data?.filter_data,
      context: data?.context,
      createdAt: data?.created_at
    };
  },

  async delete(id) {
    const { error } = await supabase?.from('saved_filters')?.delete()?.eq('id', id);
    
    if (error) throw error;
  }
};

// ============ SEARCH HISTORY SERVICE ============
export const searchHistoryService = {
  async getAll(userId, context) {
    let query = supabase?.from('search_history')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(10);

    if (context) {
      query = query?.eq('context', context);
    }
    
    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      query: row?.query,
      context: row?.context,
      createdAt: row?.created_at
    }));
  },

  async add(userId, query, context) {
    const { data, error } = await supabase?.from('search_history')?.insert({
        user_id: userId,
        query,
        context
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      userId: data?.user_id,
      query: data?.query,
      context: data?.context,
      createdAt: data?.created_at
    };
  },

  async clear(userId, context) {
    let query = supabase?.from('search_history')?.delete()?.eq('user_id', userId);

    if (context) {
      query = query?.eq('context', context);
    }
    
    const { error } = await query;
    if (error) throw error;
  }
};

// ============ REVEAL REQUESTS SERVICE ============
export const revealRequestsService = {
  async create(requestData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('reveal_requests')?.insert({
        match_id: requestData?.matchId,
        requester_id: user?.id,
        message: requestData?.message
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      matchId: data?.match_id,
      requesterId: data?.requester_id,
      status: data?.status,
      message: data?.message,
      respondedAt: data?.responded_at,
      createdAt: data?.created_at
    };
  },

  async update(id, status) {
    const { data, error } = await supabase?.from('reveal_requests')?.update({ 
        status,
        responded_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      matchId: data?.match_id,
      requesterId: data?.requester_id,
      status: data?.status,
      message: data?.message,
      respondedAt: data?.responded_at,
      createdAt: data?.created_at
    };
  }
};

// ============ INTERVIEWS SERVICE ============
export const interviewsService = {
  async create(interviewData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('interviews')?.insert({
        match_id: interviewData?.matchId,
        candidate_id: interviewData?.candidateId,
        employer_id: interviewData?.employerId,
        scheduled_at: interviewData?.scheduledAt,
        duration_minutes: interviewData?.durationMinutes || 60,
        meeting_link: interviewData?.meetingLink,
        notes: interviewData?.notes
      })?.select(`
        *,
        candidates(*, user_profiles(*)),
        employers(*, user_profiles(*)),
        matches(*, opportunities(*))
      `)?.single();
    
    if (error) throw error;

    // Schedule email reminders for both parties
    try {
      const candidatePrefs = await userPreferencesService?.get(data?.candidates?.user_id);
      const employerPrefs = await userPreferencesService?.get(data?.employers?.user_id);

      const interviewDetails = {
        title: `Interview for ${data?.matches?.opportunities?.role_title}`,
        scheduledAt: new Date(data?.scheduled_at)?.toLocaleString(),
        duration: data?.duration_minutes,
        meetingLink: data?.meeting_link,
        notes: data?.notes
      };

      // Send to candidate
      if (candidatePrefs?.notificationSettings?.emailNotifications && 
          candidatePrefs?.notificationSettings?.interviewReminders) {
        const reminderType = candidatePrefs?.notificationSettings?.interviewReminderTiming || '24hours';
        
        // Check if we should send reminder based on timing
        const shouldSendReminder = await emailNotificationService?.scheduleInterviewReminders(
          data, 
          candidatePrefs?.notificationSettings
        );

        if (shouldSendReminder) {
          await emailNotificationService?.sendInterviewReminder({
            recipientEmail: data?.candidates?.user_profiles?.email,
            recipientName: data?.candidates?.user_profiles?.full_name,
            interviewDetails,
            reminderType
          });
        }
      }

      // Send to employer
      if (employerPrefs?.notificationSettings?.emailNotifications && 
          employerPrefs?.notificationSettings?.interviewReminders) {
        const reminderType = employerPrefs?.notificationSettings?.interviewReminderTiming || '24hours';
        
        const shouldSendReminder = await emailNotificationService?.scheduleInterviewReminders(
          data, 
          employerPrefs?.notificationSettings
        );

        if (shouldSendReminder) {
          await emailNotificationService?.sendInterviewReminder({
            recipientEmail: data?.employers?.user_profiles?.email,
            recipientName: data?.employers?.user_profiles?.full_name,
            interviewDetails,
            reminderType
          });
        }
      }
    } catch (emailError) {
      console.error('Error scheduling interview reminder emails:', emailError);
      // Don't fail the interview creation if email fails
    }

    return {
      id: data?.id,
      matchId: data?.match_id,
      candidateId: data?.candidate_id,
      employerId: data?.employer_id,
      scheduledAt: data?.scheduled_at,
      durationMinutes: data?.duration_minutes,
      meetingLink: data?.meeting_link,
      notes: data?.notes,
      status: data?.status,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async update(id, updates) {
    const dbUpdates = {};
    if (updates?.scheduledAt !== undefined) dbUpdates.scheduled_at = updates?.scheduledAt;
    if (updates?.durationMinutes !== undefined) dbUpdates.duration_minutes = updates?.durationMinutes;
    if (updates?.meetingLink !== undefined) dbUpdates.meeting_link = updates?.meetingLink;
    if (updates?.notes !== undefined) dbUpdates.notes = updates?.notes;
    if (updates?.status !== undefined) dbUpdates.status = updates?.status;

    const { data, error } = await supabase?.from('interviews')?.update(dbUpdates)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      matchId: data?.match_id,
      candidateId: data?.candidate_id,
      employerId: data?.employer_id,
      scheduledAt: data?.scheduled_at,
      durationMinutes: data?.duration_minutes,
      meetingLink: data?.meeting_link,
      notes: data?.notes,
      status: data?.status,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  }
};
function userPreferencesService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: userPreferencesService is not implemented yet.', args);
  return null;
}

export { userPreferencesService };